import { useEffect, useRef, useState } from 'react';
import { useMembershipUserLookup } from '../queries';
import type { UserLookupRow } from '../api/membership.api';

type CustomerLookupComboboxProps = {
  value?: UserLookupRow | null;
  onSelect: (user: UserLookupRow) => void;
  onLookupStateChange?: (state: {
    input: string;
    normalizedPhone: string;
    isExactPhoneLookup: boolean;
    completePhoneNotFound: boolean;
  }) => void;
};

const CustomerLookupCombobox = ({ value, onSelect, onLookupStateChange }: CustomerLookupComboboxProps) => {
  const [input, setInput] = useState('');
  const [query, setQuery] = useState('');
  const lastLookupStateKey = useRef('');

  useEffect(() => {
    const timer = window.setTimeout(() => setQuery(input.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [input]);

  const digitsOnly = query.replace(/\D/g, '');
  const normalizedPhone = digitsOnly.length === 12 && digitsOnly.startsWith('91')
    ? digitsOnly.slice(2)
    : digitsOnly;
  const isPhoneLikeQuery = /^[\d\s()+-]+$/.test(query);
  const isExactPhoneLookup = /^\d{10}$/.test(normalizedPhone);
  const shouldLookup = Boolean(query)
    && ((isPhoneLikeQuery && normalizedPhone.length === 10) || (!isPhoneLikeQuery && query.length >= 2));
  const lookupQuery = useMembershipUserLookup(query, shouldLookup);
  const rows = lookupQuery.data || [];
  const completePhoneNotFound = Boolean(
    shouldLookup
    && isPhoneLikeQuery
    && isExactPhoneLookup
    && !lookupQuery.isLoading
    && rows.length === 0
  );

  useEffect(() => {
    if (!onLookupStateChange) return;
    const nextState = {
      input: query,
      normalizedPhone,
      isExactPhoneLookup: Boolean(isPhoneLikeQuery && isExactPhoneLookup),
      completePhoneNotFound
    };
    const nextStateKey = JSON.stringify(nextState);
    if (lastLookupStateKey.current === nextStateKey) return;
    lastLookupStateKey.current = nextStateKey;
    onLookupStateChange({
      ...nextState
    });
  }, [completePhoneNotFound, isExactPhoneLookup, isPhoneLikeQuery, normalizedPhone, onLookupStateChange, query]);

  return (
    <div style={{ display: 'grid', gap: 8 }}>
      <input
        className="input"
        value={value ? `${value.name} (${value.phone})` : input}
        onChange={(event) => {
          setInput(event.target.value);
        }}
        placeholder="Search customer by phone or name"
      />
      {!value && query.length > 0 ? (
        <div className="appointments-v2-card" style={{ maxHeight: 220, overflowY: 'auto' }}>
          <div className="appointments-v2-card-body" style={{ display: 'grid', gap: 6 }}>
            {lookupQuery.isLoading ? <p className="appointments-v2-empty-copy">Searching...</p> : null}
            {isPhoneLikeQuery && normalizedPhone.length > 0 && normalizedPhone.length < 10 ? (
              <p className="appointments-v2-empty-copy">Enter full 10-digit phone number.</p>
            ) : null}
            {shouldLookup && !lookupQuery.isLoading && rows.length === 0 ? (
              <p className="appointments-v2-empty-copy">No customers found.</p>
            ) : null}
            {rows.map((row) => (
              <button
                key={row.userUuid}
                type="button"
                className="btn ghost small"
                style={{ justifyContent: 'flex-start' }}
                onClick={() => onSelect(row)}
              >
                {row.name || 'Unknown'} · {row.phone || 'No phone'}
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default CustomerLookupCombobox;
